import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { Box, Typography, Switch } from "@mui/material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  parentBoxStyle,
  infoStyle,
  yearMonth,
  childBoxStyleSpaceBetween,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import {
  specialCharacterRestrict,
  onlyNumberRegex,
  specialCharacterRest,
} from "services/constants/patterns";
import {
  minimum3Characters,
  minimum10Digits,
} from "services/constants/ErrorMessages";
import moment from "moment";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { useFetch } from "services/hooks/useFetch";
import sessionHandling from "services/utils/notificationUtils";
import { setScreenUserId } from "services/Redux/HRPortal";
import { useDispatch } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { tooltipStyleProps } from "services/constants/Styles";

export const EditEmployeeBasicInfo = ({
  buttonNotify,
  employeeDetails,
  getSelectedUserDetails,
  setButtonNotify,
  setUpdateUserDetails,
  reCallApi,
  updateUserDetails,
  onClose,
}) => {
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    unregister,
    formState: { errors },
  } = useForm();
  const dispatch = useDispatch();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [checkBoxValue, setCheckBoxValue] = useState("");
  const [selectedUserCode, setSelectedUserCode] = useState("");
  const [userNameDetails, setUserNameDetails] = useState(
    employeeDetails.user_name,
  );
  const [departmentName, setDepartmentName] = useState("");
  const [shiftGradeName, setShiftGradeName] = useState("");
  const [shiftCatgName, setShiftCatgName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [employeeGroupName, setEmployeeGroupName] = useState("");
  const [designationName, setDesignationName] = useState("");
  const [dateOfJoining, setDateOfJoining] = useState(
    moment(new Date()).format(yearMonth),
  );
  const [onboardDate, setOnboardDate] = useState(
    moment(new Date()).format(yearMonth),
  );
  const [shiftGroupName, setShiftGroupName] = useState("");
  const [employeeId, setEmployeeId] = useState(employeeDetails.employee_id);
  const [leaveGroupName, setLeaveGroupName] = useState("");
  const [employmentTypeName, setEmploymentTypeName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [saveAndExit, setSaveAndExit] = useState(false);
  const { result: lastInsertedEmployeeId } = useFetch(
    `${domain}employee-id/last-used`,
  );
  const { result: department } = useFetch(`${domain}department/`);
  const { result: designation } = useFetch(`${domain}designation/`);
  const { result: employmentType } = useFetch(`${domain}employment-type/`);
  const { result: companyList } = useFetch(`${domain}get-active-company/`);
  const { result: userGroupList } = useFetch(
    `${domain}basic/user-group/details/`,
  );
  const { result: employeeGroup } = useFetch(`${domain}employee-group/`);

  useEffect(() => {
    if (buttonNotify !== "Basic Info") return;
    if (!employeeDetails) return;

    const updateData = () => {
      setValue("user_id", employeeDetails.user_id);
      setValue("user_code", employeeDetails.user_code);
      setValue("userName", employeeDetails.user_name);
      setValue("edit_user_name", employeeDetails.user_name);
      setValue("employee_id", employeeDetails.employee_id);
      setValue("company", employeeDetails.company_name);
      setValue("company_id", employeeDetails.company_id);
      setValue("departmentName", employeeDetails.department_name);
      setValue("department_id", employeeDetails.department_id);
      setValue("leaveGroup", employeeDetails.leave_group_name);
      setValue("leave_group_id", employeeDetails.leave_group_id);
      setValue("leaveGroupCount", employeeDetails.overall_leave_count);
      setValue("phone_number", employeeDetails.phone_number);
      setValue("email", employeeDetails.email);
      setValue("designation", employeeDetails.designation);
      setValue("designation_id", employeeDetails.designation_id);
      setValue("employmentType", employeeDetails?.employment_type);
      setValue("employment_type_id", employeeDetails?.employment_type_id);
      setValue("user_group_name", employeeDetails?.user_group_name);
      setValue("user_group", employeeDetails?.user_group);
      setValue("shift_group", employeeDetails?.shift_group);
      setValue("shiftGroup", employeeDetails?.shift_group_name);

      setShiftGroupName(employeeDetails?.shift_group_name);

      setValue(
        "date_joined",
        employeeDetails?.date_joined
          ? employeeDetails.date_joined.split("-").reverse().join("-")
          : "",
      );

      setValue(
        "onboard_date",
        employeeDetails?.onboard_date
          ? employeeDetails.onboard_date.split("-").reverse().join("-")
          : "",
      );

      setValue("employee_group", employeeDetails?.employee_group);
      setValue("employee_group_id", employeeDetails?.employee_group_id);
      setValue("shiftGradeName", employeeDetails?.shift_grade_name);
      setValue("shift_grade", employeeDetails?.shift_grade);
      setValue("shiftCatgName", employeeDetails?.shift_category_name);
      setValue("shift_category", employeeDetails?.shift_category);

      // local states
      setUpdateUserDetails(false);
      setUserNameDetails(employeeDetails?.user_name);
      setSelectedUserCode(employeeDetails?.user_code);
      setDepartmentName(employeeDetails?.department_name);
      setShiftGradeName(employeeDetails?.shift_grade_name);
      setShiftCatgName(employeeDetails?.shift_category_name);
      setCompanyName(employeeDetails?.company_name);
      setDesignationName(employeeDetails?.designation);
      setDateOfJoining(
        employeeDetails?.date_joined
          ? employeeDetails.date_joined.split("-").reverse().join("-")
          : "",
      );
      setOnboardDate(
        employeeDetails?.onboard_date
          ? employeeDetails.onboard_date.split("-").reverse().join("-")
          : "",
      );
      setEmployeeId(employeeDetails?.employee_id);
      setCheckBoxValue(employeeDetails?.portal_access);
      setLeaveGroupName(employeeDetails?.leave_group_name);
      setEmploymentTypeName(employeeDetails?.employment_type);
      setPhoneNumber(employeeDetails?.phone_number);
      setEmployeeGroupName(employeeDetails?.employee_group);
    };

    updateData();
  }, [
    updateUserDetails,
    buttonNotify,
    employeeDetails,
    setUpdateUserDetails,
    setValue,
  ]);

  const updateUserName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 32
    ) {
      setUserNameDetails(event.target.value);
      setValue("edit_user_name", event.target.value);
    }
  };

  const handleEmployeeId = (event) => {
    if (
      specialCharacterRest.test(event.target.value) &&
      event.target.value.length <= 12
    ) {
      setValue("employee_id", event.target.value);
      setEmployeeId(event.target.value);
    }
  };

  const handleCompanyName = (event, data) => {
    if (data) {
      setValue("company", data?.label);
      setValue("company_id", data?.id);
      setCompanyName(data?.label);
    } else {
      setValue("company", "");
      setValue("company_id", null);
      setCompanyName("");
    }
  };

  const handleEmployeeGroup = (event, data) => {
    if (data) {
      setValue("employee_group", data?.label);
      setValue("employee_group_id", data?.group_id);
      setEmployeeGroupName(data?.label);
    } else {
      setValue("employee_group", "");
      setValue("employee_group_id", null);
      setEmployeeGroupName("");
    }
  };

  const handleDepartment = async (event, data) => {
    if (data) {
      setValue("departmentName", data?.label);
      setValue("department_id", data?.id);
      setDepartmentName(data?.label);
    } else {
      setValue("departmentName", "");
      setValue("department_id", null);
      setDepartmentName("");
    }
  };

  const handleDesignation = (event, data) => {
    if (data) {
      setValue("designation", data?.label);
      setValue("designation_id", data?.id);
      setDesignationName(data?.label);
    } else {
      setValue("designation", "");
      setValue("designation_id", null);
      setDesignationName("");
    }
  };

  const handleDateOfJoined = (event) => {
    setValue("date_joined", event.target.value);
    setDateOfJoining(event.target.value);
    setValue("onboard_date", event.target.value);
    setOnboardDate(event.target.value);
  };

  const handleOnboardDate = (event) => {
    setValue("onboard_date", event.target.value);
    setOnboardDate(event.target.value);
  };

  const handleEmploymentType = (event, data) => {
    if (data) {
      setValue("employmentType", data?.label);
      setValue("employment_type_id", data?.id);
      setEmploymentTypeName(data?.label);
    } else {
      setValue("employmentType", "");
      setValue("employment_type_id", null);
      setEmploymentTypeName("");
    }
  };

  const updateCheckBoxValue = (event) => {
    setCheckBoxValue(event.target.checked);
    setValue("user_group_name", "");
    setValue("user_group", "");
    if (!event.target.checked) {
      clearErrors("user_group_name");
      unregister("user_group_name");
    }
  };

  const handleUserGroupName = (event, data) => {
    if (data) {
      setValue("user_group_name", data?.label);
      setValue("user_group", data?.id);
    } else {
      setValue("user_group_name", "");
      setValue("user_group", "");
    }
  };

  const updatePhoneNumber = (event) => {
    if (
      event.target.value.length <= 15 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setPhoneNumber(event.target.value);
      setValue("phone_number", event.target.value);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      portal_access: checkBoxValue,
      edit_user_name: data?.edit_user_name.trim(),
      user_code: parseInt(data?.user_code),
    };
    const response = await customAPI(
      `${domain}employee-onboard`,
      "PUT",
      payload,
    );
    if (response.ok) {
      if (saveAndExit) {
        showToast("success", "Employee details updated successfully");
        reset();
        reCallApi(true);
        onClose();
      } else {
        reset();
        dispatch(setScreenUserId(employeeDetails?.user_id));
        getSelectedUserDetails(employeeDetails?.user_id);
        setButtonNotify("Personal Info");
      }
    } else {
      setSaveAndExit(false);
    }
  };

  const updateUserCode = (event) => {
    if (
      event.target.value.length <= 10 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setSelectedUserCode(event.target.value);
      setValue("user_code", event.target.value);
    }
  };

  return (
    <Box sx={parentBoxStyle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 2, m: 2, border: "none" }}>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={12}>
              <Typography sx={{ fontWeight: 600 }}>Company</Typography>
              <Typography {...infoStyle}>
                Select the company entity the employee belongs to.
              </Typography>
              <CustomAutoComplete
                dataList={companyList.map((obj) => ({
                  ...obj,
                  label: obj.companyname,
                }))}
                selectedValue={companyName}
                rules={{
                  ...register("company", {
                    required: "Company name is required",
                  }),
                }}
                updateDataFunction={handleCompanyName}
                placeHolder="Select the company name"
                errorMessage={
                  errors?.company?.message ? errors?.company?.message : ""
                }
                textBoxError={errors.company ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Box>
        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Basic Information
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Employee Name</Typography>
              <Typography {...infoStyle}>
                Full legal name of the employee.
              </Typography>
              <CustomTextBox
                selectedValue={userNameDetails}
                rules={{
                  ...register("edit_user_name", {
                    required: "Username is required",
                    maxLength: {
                      value: 32,
                      message: "Maximum 32 characters are required",
                    },
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else {
                          return true;
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateUserName}
                placeHolder="Enter the Name"
                errorMessage={
                  errors?.edit_user_name?.message
                    ? errors?.edit_user_name?.message
                    : ""
                }
                textBoxError={errors.edit_user_name ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>
                Employee IdEmployee Id &nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title={`Last Used : ${lastInsertedEmployeeId.latest_employee_id}`}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 16,
                      p: 0,
                      m: 0,
                      mt: "-2px",
                      cursor: "pointer",
                      verticalAlign: "middle",
                      color: "text.secondary",
                    }}
                  />
                </Mui.Tooltip>
              </Typography>
              <Typography {...infoStyle}>
                Unique id number assigned to the employee.
              </Typography>

              <CustomTextBox
                selectedValue={employeeId}
                rules={{
                  ...register("employee_id", {
                    required: "Employee id is required",
                    minLength: {
                      value: 3,
                      message: "Minimum 3 characters are required",
                    },
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else {
                          return true;
                        }
                      },
                      employeeIdAvailable: async (fieldValue) => {
                        if (
                          fieldValue !== "" &&
                          fieldValue !== employeeDetails.employee_id
                        ) {
                          const response = await fetch(
                            `${domain}employee-id/check?employee_id=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "Employee Id Already Exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={handleEmployeeId}
                placeHolder="Enter the employee id"
                errorMessage={
                  errors?.employee_id?.message
                    ? errors?.employee_id?.message
                    : ""
                }
                textBoxError={errors.employee_id ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Date of Joining</Typography>
              <Typography {...infoStyle}>
                The official date to starts working.
              </Typography>
              <CustomTextBox
                type="date"
                selectedValue={dateOfJoining}
                rules={{
                  ...register("date_joined", {
                    required: "Date of joined is required",
                  }),
                }}
                updateDataFunction={handleDateOfJoined}
                errorMessage={
                  errors?.date_joined?.message
                    ? errors?.date_joined?.message
                    : ""
                }
                textBoxError={errors.date_joined ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Onboarding Date</Typography>
              <Typography {...infoStyle}>
                The official date to onboarding.
              </Typography>
              <CustomTextBox
                type="date"
                selectedValue={onboardDate}
                minDate={dateOfJoining}
                rules={{
                  ...register("onboard_date", {
                    required: "Onboard date is required",
                    validate: (value) =>
                      !dateOfJoining ||
                      new Date(value) >= new Date(dateOfJoining) ||
                      "Onboarding date cannot be before Date of Joining",
                  }),
                }}
                updateDataFunction={handleOnboardDate}
                errorMessage={
                  errors?.onboard_date?.message
                    ? errors?.onboard_date?.message
                    : ""
                }
                textBoxError={errors.onboard_date ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Phone No</Typography>
              <Typography {...infoStyle}>
                Primary mobile number for communication.
              </Typography>
              <CustomTextBox
                selectedValue={phoneNumber}
                rules={{
                  ...register("phone_number", {
                    required: "Phone number is required",
                    minLength: {
                      value: 10,
                      message: minimum10Digits,
                    },
                    validate: {
                      employeePhonenumberAvailable: async (fieldValue) => {
                        if (
                          fieldValue !== "" &&
                          fieldValue !== employeeDetails.phone_number
                        ) {
                          const response = await fetch(
                            `${domain}unique/phone-number/?phone_no=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "Phone number already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updatePhoneNumber}
                placeHolder="Enter the phone number"
                errorMessage={
                  errors?.phone_number?.message
                    ? errors?.phone_number?.message
                    : ""
                }
                textBoxError={errors.phone_number ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Email ID</Typography>
              <Typography {...infoStyle}>
                Official email ID used for communication.
              </Typography>
              <Mui.TextField
                size="small"
                {...register("email", {
                  required: false,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid e-mail address",
                  },
                  validate: {
                    emailAvailable: async (fieldValue) => {
                      if (
                        fieldValue !== "" &&
                        fieldValue !== employeeDetails.email
                      ) {
                        try {
                          const response = await fetch(
                            `${domain}unique/email/?email=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const res = await response.json();
                          if (response.ok) {
                            if (res.exists) {
                              return "Email Already Exists";
                            } else {
                              return true;
                            }
                          } else if (response.status === 409) {
                            sessionHandling();
                          } else {
                            throw new Error(res.error);
                          }
                        } catch (error) {
                          showToast("error", error.message);
                        }
                      }
                    },
                  },
                })}
                error={Boolean(errors.email)}
                helperText={errors.email ? errors.email.message : null}
                placeholder="Enter the email"
                fullWidth
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>User Code</Typography>
              <Typography {...infoStyle}>
                Unique code assigned to the employee.
              </Typography>
              <CustomTextBox
                selectedValue={selectedUserCode}
                rules={{
                  ...register("user_code", {
                    required: "User code is required",
                    maxLength: {
                      value: 10,
                      message: "Minimum 10 characters are required",
                    },
                    validate: {
                      employeeIdAvailable: async (fieldValue) => {
                        if (
                          fieldValue !== "" &&
                          fieldValue !== employeeDetails.user_code
                        ) {
                          const response = await fetch(
                            `${domain}unique/user-code/?user_code=${parseInt(
                              fieldValue,
                            )}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "User Code Already Exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateUserCode}
                placeHolder="Enter the user code"
                errorMessage={
                  errors?.user_code?.message ? errors?.user_code?.message : ""
                }
                textBoxError={errors.user_code ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Employment Information
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Employee Group</Typography>
              <Typography {...infoStyle}>Select the employee group.</Typography>
              <CustomAutoComplete
                dataList={employeeGroup.map((obj) => ({
                  ...obj,
                  label: obj.group_name,
                }))}
                selectedValue={employeeGroupName}
                rules={{
                  ...register("employee_group", {
                    required: "Employee group is required",
                  }),
                }}
                updateDataFunction={handleEmployeeGroup}
                placeHolder="Select the employee group"
                errorMessage={
                  errors?.employee_group?.message
                    ? errors?.employee_group?.message
                    : ""
                }
                textBoxError={errors.employee_group ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Employment Type</Typography>
              <Typography {...infoStyle}>
                Choose the work type like Full-time.
              </Typography>
              <CustomAutoComplete
                dataList={employmentType.map((obj) => ({
                  ...obj,
                  label: obj.employment_type,
                }))}
                selectedValue={employmentTypeName}
                rules={{
                  ...register("employmentType", {
                    required: "Employment Type is required",
                  }),
                }}
                updateDataFunction={handleEmploymentType}
                placeHolder="Select the employment type"
                errorMessage={
                  errors?.employmentType?.message
                    ? errors?.employmentType?.message
                    : ""
                }
                textBoxError={errors.employmentType ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Department</Typography>
              <Typography {...infoStyle}>
                Where the employee will work.
              </Typography>
              <CustomAutoComplete
                dataList={department.map((obj) => ({
                  ...obj,
                  label: obj.department,
                }))}
                selectedValue={departmentName}
                rules={{
                  ...register("departmentName", {
                    required: "Department name is required",
                  }),
                }}
                updateDataFunction={handleDepartment}
                placeHolder="Select the department name"
                errorMessage={
                  errors?.departmentName?.message
                    ? errors?.departmentName?.message
                    : ""
                }
                textBoxError={errors?.departmentName ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Designation</Typography>
              <Typography {...infoStyle}>
                Job title within the organization.
              </Typography>
              <CustomAutoComplete
                dataList={designation.map((obj) => ({
                  ...obj,
                  label: obj.designation,
                }))}
                selectedValue={designationName}
                rules={{
                  ...register("designation", {
                    required: "Designation is required",
                  }),
                }}
                updateDataFunction={handleDesignation}
                placeHolder="Select the designation"
                errorMessage={
                  errors?.designation?.message
                    ? errors?.designation?.message
                    : ""
                }
                textBoxError={errors?.designation ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Shift & Leave Information
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Shift Grade</Typography>
              <Typography {...infoStyle}>
                Shift level defines working time rules.
              </Typography>
              <Mui.TextField size="small" value={shiftGradeName} fullWidth />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Shift Group</Typography>
              <Typography {...infoStyle}>
                Group of shifts based on work pattern.
              </Typography>
              <Mui.TextField size="small" value={shiftGroupName} fullWidth />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Shift Category</Typography>
              <Typography {...infoStyle}>
                Categorized shift type based on role.
              </Typography>
              <Mui.TextField size="small" value={shiftCatgName} fullWidth />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Leave Group</Typography>
              <Typography {...infoStyle}>
                Leave policy assigned to the employee.
              </Typography>
              <Mui.TextField size="small" value={leaveGroupName} fullWidth />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Access & Permissions
          </Mui.Typography>
          <Box sx={childBoxStyleSpaceBetween}>
            <Switch checked={checkBoxValue} onChange={updateCheckBoxValue} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 600 }}>
                Enable Portal Access
              </Typography>
              <Typography {...infoStyle}>
                The employee will be able to submit their leaves, reimbursement
                claims, view, and generate payslips through the employee portal.
              </Typography>
            </Box>
          </Box>
          {checkBoxValue && (
            <Mui.Grid item xs={12} sx={{ p: 2, mt: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>User Group</Typography>
              <Typography {...infoStyle}>
                Select the user group to define what features and permissions
                the employee can access when using the portal.
              </Typography>
              <CustomAutoComplete
                dataList={userGroupList.map((obj) => ({
                  ...obj,
                  label: obj.name,
                }))}
                selectedValue={watch("user_group_name")}
                rules={{
                  ...register("user_group_name", {
                    required: checkBoxValue
                      ? "User Group name is required"
                      : false,
                  }),
                }}
                updateDataFunction={handleUserGroupName}
                placeHolder="Select the user group name"
                errorMessage={
                  errors?.user_group_name?.message
                    ? errors?.user_group_name?.message
                    : ""
                }
                textBoxError={errors?.user_group_name ? true : false}
              />
            </Mui.Grid>
          )}
        </Mui.Paper>

        <Mui.Grid
          container
          sx={{
            paddingBottom: { xs: 2, sm: 4, md: 4, lg: 5, xl: 5, xxl: 5 },
          }}
        />
        <Mui.Grid container sx={{ padding: 2 }}>
          <Mui.Grid xs={3}>
            <CustomButton
              actionFuntion={() => {
                setSaveAndExit(true);
              }}
              actionName="Save & Exit"
              typeName="submit"
            />
          </Mui.Grid>
          <Mui.Grid xs={6}></Mui.Grid>
          <Mui.Grid
            xs={3}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <CustomButton actionName="Next" typeName="submit" />
          </Mui.Grid>
        </Mui.Grid>
      </form>
    </Box>
  );
};
